import {
    ElementRef,
    Host,
    Input,
    OnDestroy,
} from '@angular/core';
import {
    MatAnchor,
    MatButton,
} from '@angular/material/button';
import { MatMenuItem } from '@angular/material/menu';
import { AppStateFacade } from '@app-state';
import { TranslateService } from '@ngx-translate/core';
import { unsubscribe } from '@utils/object.utils';

import {
    combineLatest,
    Observable,
    Subscription,
} from 'rxjs';

export abstract class AuthorizeBaseDirective implements OnDestroy {
    private _element: HTMLElement;
    private _originalTitle: string | null = null;
    private _originalDisplay: string | null = null;
    private _isPermissionDisable?: boolean;
    private _disableIf = false;

    private _hideSub?: Subscription;
    private _disableSub?: Subscription;

    @Input()
    public set disableIf(condition: boolean) {
        this._disableIf = condition;
        this._isElementDisabled = condition || !!this._isPermissionDisable;
    }

    private set _isElementDisabled(state: boolean) {
        if (state) {
            this._element.setAttribute('disabled', 'true');
        } else {
            this._element.removeAttribute('disabled');
        }
        if (this._hostButton) {
            this._hostButton.disabled = state;
        }
        if (this._hostAnchor) {
            this._hostAnchor.disabled = state;
        }
        if (this._hostMenuItem) {
            this._hostMenuItem.disabled = state;
        }
    }


    constructor(
        protected _appState: AppStateFacade,
        @Host() private _hostButton: MatButton,
        @Host() private _hostAnchor: MatAnchor,
        @Host() private _hostMenuItem: MatMenuItem,
        private _translateService: TranslateService,
        element: ElementRef,
    ) {
        this._element = element.nativeElement;
    }

    ngOnDestroy() {
        unsubscribe(
            this._hideSub,
            this._disableSub,
        );
    }

    protected set _permission(permission: string) {
        if (this._originalDisplay == null) {
            this._originalDisplay = this._element.style.display;
        }
        if (this._hideSub) { this._hideSub.unsubscribe(); }
        this._hideSub =
            this._isGranted(permission).subscribe((result) => {
                if (!result) {
                    this._element.style.display = 'none';
                } else {
                    this._element.style.display = this._originalDisplay;
                }
            });
    }

    protected set _permissionDisable(permission: string) {
        if (this._originalTitle == null) {
            this._originalTitle = this._element.style.display;
        }

        if (this._disableSub) { this._disableSub.unsubscribe(); }

        this._disableSub = combineLatest([
            this._translateService.stream('authorize_no_permissions'),
            this._isGranted(permission),
        ])
            .subscribe(([message, granted]) => {
                this._isPermissionDisable = !granted;
                if (this._isPermissionDisable) {
                    this._element.title = message;
                    this._isElementDisabled = true;
                } else {
                    this._element.title = this._originalTitle || '';
                    this._isElementDisabled = this._disableIf;
                }
            });
    }

    protected set _permissionAny(permissions: string[]) {
        this._permission = permissions.join('|');
    }

    protected set _permissionAnyDisable(permissions: string[]) {
        this._permissionDisable = permissions.join('|');
    }

    protected set _permissionAll(permissions: string[]) {
        this._permission = permissions.join('&');
    }

    protected set _permissionAllDisable(permissions: string[]) {
        this._permissionDisable = permissions.join('&');
    }

    protected abstract _isGranted(permission: string): Observable<boolean>;
}
